import { useCallback, useContext, useEffect, useState } from 'react';

import { PRISMAX_BACKEND_URL } from '../../configs/config';
import { SwapMainAppContext } from "../../context/SwapMainAppContext";

const REASON_MESSAGES = {
    'access_denied': 'Discord authorization was cancelled.',
    'token_mismatch': 'Your session changed during linking. Please try again.',
    'expired_state': 'Your linking session expired. Please start again.',
    'token_request_failed': 'Unable to reach Discord. Please try again shortly.',
    'token_exchange_error': 'Discord did not accept the authorization code. Please retry.',
    'missing_access_token': 'Discord did not provide an access token.',
    'user_info_failed': 'Unable to fetch your Discord profile. Please try again.',
    'user_info_error': 'Discord rejected the profile request. Please retry.',
    'missing_user_id': 'Discord did not return an account identifier.',
    'user_not_found': 'We could not find your account session. Please sign in again.',
    'missing_state': 'Invalid Discord session. Please try linking again.',
    'invalid_state': 'Invalid Discord session. Please try linking again.',
    'missing_code': 'Discord did not return a verification code. Please try again.'
};

const isMobileDevice = () => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

export function useDiscordOAuth2({
    fetchUserInfo,
    setUserInfo,
    setEditedInfo,
    userInfo,
    editedInfo,
    formatDisplayValue,
    formatEditValue
}) {

    const [isLinkingDiscord, setIsLinkingDiscord] = useState(false);
    const {
        selectedChain,
        showNotification
    } = useContext(SwapMainAppContext);
    
    // -------------------------------------------------------------------------
    // Popup callback handler — runs when /account is loaded inside the popup
    // after Discord redirects back. Posts result to opener then closes itself.
    // -------------------------------------------------------------------------
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const discordStatus = params.get('discord_status');

        if (!discordStatus) return;

        const reason = params.get('discord_reason') || '';
        const username = params.get('discord_username');

        // If we're inside a popup, relay the result to the opener and close
        if (window.opener && !window.opener.closed) {
            window.opener.postMessage(
                { type: 'DISCORD_OAUTH_RESULT', discordStatus, reason, username },
                window.location.origin
            );
            window.close();
            return;
        }

        // Fallback: page was opened normally (e.g. mobile redirect), handle inline
        if (discordStatus === 'success') {
            const successMessage = username
                ? `Discord account ${username} linked successfully.`
                : 'Discord account linked successfully.';
            showNotification('success', successMessage);
            fetchUserInfo();
        } else {
            const errorMessage = REASON_MESSAGES[reason] || 'Unable to link your Discord account. Please try again.';
            showNotification('error', errorMessage);
        }

        // Clean up query params from the URL
        params.delete('discord_status');
        params.delete('discord_reason');
        params.delete('discord_username');
        const filteredSearch = params.toString();
        const newUrl = `${window.location.pathname}${filteredSearch ? `?${filteredSearch}` : ''}${window.location.hash}`;
        window.history.replaceState({}, '', newUrl);
    }, [fetchUserInfo, showNotification]);

    // -------------------------------------------------------------------------
    // Link handler
    // -------------------------------------------------------------------------
    const handleDiscordLink = useCallback(async () => {
        const token = localStorage.getItem('gatewayToken');
        if (!token) {
            showNotification('error', 'Authentication token missing. Please log in again.');
            return;
        }

        const storedEmail = localStorage.getItem('userEmail') || '';
        const storedWalletAddress = localStorage.getItem('walletAddress') || '';

        if (!storedEmail && !storedWalletAddress) {
            showNotification('error', 'No account information found. Please sign in again.');
            return;
        }

        const returnUrl = `${window.location.origin}/account`;

        const requestBody = {
            chain: (selectedChain || 'solana'),
            return_url: returnUrl,
            backend_host_url: PRISMAX_BACKEND_URL
        };

        if (storedEmail) {
            requestBody.email = storedEmail.toLowerCase();
        } else {
            requestBody.wallet_address = storedWalletAddress;
        }

        const openCenteredPopup = () => {
            const width = 450;
            const height = 700;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;
            const features = `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes,location=no,status=no`;

            const popup = window.open('about:blank', 'DiscordOAuth', features);
            if (!popup) return null;

            try {
                popup.document.write(
                    '<!DOCTYPE html><html><head><title>Prismax</title></head>' +
                    '<body style="background:#030712;color:#FFFFFF;font-family:Arial,Helvetica,sans-serif;' +
                    'display:flex;align-items:center;justify-content:center;height:100vh;margin:0;">' +
                    '<p>Redirecting to Discord...</p>' +
                    '</body></html>'
                );
                popup.document.close();
            } catch (e) {
                console.warn('Unable to write to Discord popup window.', e);
            }

            return popup;
        };

        // Register the postMessage listener before opening the popup
        const handleOAuthMessage = (event) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type !== 'DISCORD_OAUTH_RESULT') return;

            window.removeEventListener('message', handleOAuthMessage);

            const { discordStatus, reason, username } = event.data;

            if (discordStatus === 'success') {
                const successMessage = username
                    ? `Discord account ${username} linked successfully.`
                    : 'Discord account linked successfully.';
                showNotification('success', successMessage);
                fetchUserInfo();
            } else {
                const errorMessage = REASON_MESSAGES[reason] || 'Unable to link your Discord account. Please try again.';
                showNotification('error', errorMessage);
            }
        };
        window.addEventListener('message', handleOAuthMessage);

        const isMobile = isMobileDevice();
        const popupWindow = isMobile ? null : openCenteredPopup();

        try {
            setIsLinkingDiscord(true);

            const response = await fetch(`${PRISMAX_BACKEND_URL}/auth/discord/initiate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            const result = await response.json().catch(() => ({}));

            if (response.ok && result.success && result.authorization_url) {
                if (popupWindow && !popupWindow.closed) {
                    popupWindow.location.href = result.authorization_url;
                    popupWindow.focus();
                } else {
                    // Fallback: mobile or popup was blocked — listener not needed
                    window.removeEventListener('message', handleOAuthMessage);
                    window.location.href = result.authorization_url;
                }
                return;
            }

            window.removeEventListener('message', handleOAuthMessage);
            if (popupWindow && !popupWindow.closed) popupWindow.close();

            const errorMsg = result?.msg || 'Unable to start Discord verification. Please try again.';
            showNotification('error', errorMsg);
        } catch (err) {
            window.removeEventListener('message', handleOAuthMessage);
            if (popupWindow && !popupWindow.closed) popupWindow.close();
            console.error('Error initiating Discord OAuth:', err);
            showNotification('error', 'Unable to start Discord verification. Please try again.');
        } finally {
            setIsLinkingDiscord(false);
        }
    }, [selectedChain, showNotification, fetchUserInfo, setIsLinkingDiscord, PRISMAX_BACKEND_URL]);

    // -------------------------------------------------------------------------
    // Unlink handler
    // -------------------------------------------------------------------------
    const handleDiscordUnlink = useCallback(async () => {
        try {
            const token = localStorage.getItem('gatewayToken');
            const userEmail = localStorage.getItem('userEmail');
            const walletAddress = localStorage.getItem('walletAddress');
            const chain = selectedChain || 'solana';

            if (!token) {
                showNotification('error', 'Authentication token missing. Please log in again.');
                return;
            }

            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/user-profile/discord-unlink`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: userEmail,
                    wallet_address: walletAddress,
                    chain
                })
            });

            if (!response.ok) {
                throw new Error(response.statusText);
            }

            const result = await response.json();
            if (!result.success) {
                throw new Error(`Did not receive success from discord unlink API ${result?.msg}`);
            }

            setUserInfo({
                ...userInfo,
                discordId: formatDisplayValue(''),
                discordName: formatDisplayValue('')
            });
            setEditedInfo({
                ...editedInfo,
                discordId: formatEditValue(''),
                discordName: formatEditValue('')
            });
            showNotification(
                'success',
                `
                    <div>
                        Successfully removed your Discord account from your profile. Visit this link to manage your authorized apps
                            <a
                                href="https://discord.com/settings/authorized-apps"
                                target="_blank" 
                                rel="noopener noreferrer"
                            >
                                <div style="display: flex; flex-direction: row; align-items: center; gap: 3px">
                                    <span>https://discord.com/settings/authorized-apps</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-external-link-icon lucide-external-link"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>
                                </div>
                            </a>
                    </div>
                `,
                20000
            );
        } catch (error) {
            console.error('Error unlinking Discord account:', error);
            showNotification('error', 'Failed to unlink your Discord account. Please try again.');
        }
    }, [selectedChain, showNotification, setUserInfo, setEditedInfo, userInfo, editedInfo, formatDisplayValue, formatEditValue, PRISMAX_BACKEND_URL]);

    return { handleDiscordLink, handleDiscordUnlink, isLinkingDiscord };
}